import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/api";

/**
 * Address suggestions for a meeting's location, debounced.
 *
 * Debounced because Places bills per lookup and a keystroke is not a question.
 * 350ms is long enough that typing a postcode is one call rather than seven,
 * and short enough that it still feels like it is keeping up.
 *
 * The suggestions are always advisory. The field underneath stays free text,
 * and if the server has no Places key this returns nothing and the box behaves
 * exactly as it did before -- "their Rotherham branch" is a perfectly good
 * answer that no address API will ever suggest.
 */
export function useAddressSuggestions(query: string): string[] {
  const [debounced, setDebounced] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 350);
    return () => clearTimeout(timer);
  }, [query]);

  const suggestions = useQuery({
    queryKey: ["google", "places", debounced],
    queryFn: () => api.placeSuggestions(debounced),
    /* Three characters is where a suggestion starts being about a place rather
       than about the alphabet. It is also the server's own minimum. */
    enabled: debounced.trim().length >= 3,
    /* Addresses do not move. Caching for the session means going back to a
       field you have already used costs nothing. */
    staleTime: 30 * 60_000,
    /* A lookup that fails should leave the field usable, not retry three times
       and bill for it. */
    retry: false,
  });

  return (suggestions.data?.suggestions ?? []).map((entry) => entry.text);
}
